import { type SiteId } from '@solinkcorp/api-types';
import { useUserHasFeature, useUserSession } from '@solinkcorp/authentication';
import { ALL_LOCATIONS } from '@solinkcorp/vd-common';
import MonitorCenterApi from 'clients/MonitorCentreApi';
import { type IEvent } from 'clients/PlatformApi';
import React from 'react';
import useSWR, { type KeyedMutator } from 'swr';
import { isAccessControl } from 'utils/EventUtil';

const POLLING_INTERVAL = 2000;

export interface UseOpenAlarmEvents {
  eventsByLocation: Record<SiteId, IEvent[]> | undefined;
  data:
    | {
        results: IEvent[];
      }
    | undefined;
  error: any;
  mutate: KeyedMutator<
    | {
        results: IEvent[];
      }
    | undefined
  >;
  isValidating: boolean;
  isLoading: boolean;
  openAlarmsCount: number;
}

export default function useOpenAlarmEvents({
  jwt,
  fetchingDisabled = false,
  filter,
}: {
  jwt: string;
  fetchingDisabled?: boolean;
  filter?: { [key: string]: unknown } | undefined;
}): UseOpenAlarmEvents {
  // Serialize the filter object to a string so that it can be used as a key in the SWR cache
  // This ensures that the cache is invalidated when the filter object changes
  const serializedFilter = React.useMemo(() => JSON.stringify(filter), [filter]);

  const user = useUserSession();
  const canManageAccessControl = useUserHasFeature('canManageAccessControl');

  const openAlarmEvents = useSWR(
    fetchingDisabled ? null : ['openAlarmEvents', serializedFilter, user.profile.verifEyeGroupIds],
    () => MonitorCenterApi.getOpenAlarmEvents(jwt, filter, user.profile.verifEyeGroupIds),
    {
      refreshInterval: POLLING_INTERVAL,
    },
  );

  const openAlarmsCount = openAlarmEvents?.data?.results?.length || 0;

  // TODO: Remove this once the platform API is updated to include the isAccessRequestEvent flag
  openAlarmEvents.data?.results?.forEach((event: IEvent) => {
    if (canManageAccessControl && isAccessControl(event)) {
      event.details ??= {};
      event.details.isAccessRequestEvent = true;
    }
  });

  // Define the type for the accumulator as a Record
  // where each key is a SiteId, and each value is an array of IEvent objects
  const eventsByLocation: Record<SiteId, IEvent[]> | undefined =
    openAlarmEvents?.data?.results?.reduce((acc: Record<SiteId, IEvent[]>, event: IEvent) => {
      const siteId = event.siteId;

      if (!siteId) {
        return acc;
      }

      acc[siteId] = acc[siteId] ? [...acc[siteId], event] : [event];
      acc[ALL_LOCATIONS as SiteId] = acc[ALL_LOCATIONS as SiteId]
        ? [...acc[ALL_LOCATIONS as SiteId], event]
        : [event];

      return acc;
    }, {});

  return { ...openAlarmEvents, eventsByLocation, openAlarmsCount };
}
